import * as THREE from "three";
import CameraControls from "camera-controls";

import { parseUrlQuery } from "../../utils/url";
import { Pane } from "tweakpane";
import { BaseScene } from "./generic/BaseScene";
import { Loader } from "./generic/loaders";
import { StandardScene } from "./generic/StandardScene";

import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { EventsManager } from "../events/EventsManager";
import { isBrowser, isMobile } from "../../utils/platform";
// import { EnvironmentManager } from "./EnvironmentManager";
import { availableFeatures, supportedLanguages } from "data/content.types";
import { EXTERNAL_AVAILABLE_FEATURES, IFeatureData, INTERNAL_AVAILABLE_FEATURES, TOTAL_FEATURES } from "data/features";
import { SpriteManager } from "./objects/SpriteManager";
import { Vector3 } from "three";
import { getGPUTier, TierResult } from "detect-gpu";
import { StatsManager } from "./utils/StatsManager";
import { COPY } from "data/content";

CameraControls.install({ THREE: THREE });

interface FeatureTargets {
  [name: string]: THREE.Mesh[]
}

export class ScenesManager {
  static canvas: HTMLCanvasElement;
  static mainScene: BaseScene;
  static raycaster: THREE.Raycaster;
  static pointer: THREE.Vector2;
  static allCreated: boolean;
  static envMap: THREE.Texture;
  static tweakPane: any;
  static editFeaturesPane: any;
  static clock: THREE.Clock;
  static publicURL: string = process.env.PUBLIC_URL || "";
  static basePath = isBrowser() ? window.location.origin + ScenesManager.publicURL : "";
  static mouse = {
    x: 0,
    y: 0,
  };
  static windowHalfX = window.innerWidth / 2;
  static windowHalfY = window.innerHeight / 2;
  static followMouseEnabled = false;
  static inTransition = false;

  static featureHits: THREE.Object3D[] = [];
  static featureLights: THREE.Object3D[] = [];
  static featureTraveling: string[] = [];
  static featureTravelingInterior: string[] = [];
  static featureTravelingParticles:  {[name: string]: { targets: THREE.Mesh[] }} = {};
  static featureActiveObjs: FeatureTargets = {};
  static featureCameraTargets: {[name: string]: { target: THREE.Mesh[] }} = {};
  static lastAddedFeature: {
    name: string;
    active: boolean;
    lastEditedTime: string;
    coordinates: {
      desktop: { pos: Vector3; lookAt: Vector3 };
      mobile?: { pos: Vector3; lookAt: Vector3 };
    }
  } | null = null
  static inFeature: boolean = false;
  static inInterior: boolean = false;
  static currentFeature2D: THREE.Vector2 = new THREE.Vector2();
  static currentFeatureName: 
    availableFeatures.somBose |
    availableFeatures.assinaturaDeDesign |
    availableFeatures.soleira |
    availableFeatures.cambio |
    availableFeatures.coluna |
    availableFeatures.retrovisor |
    availableFeatures.farolGrade |
    availableFeatures.lanternaTraseira |
    availableFeatures.tetoSolar |
    availableFeatures.telas |
    availableFeatures.biTone |
    availableFeatures.rodas | '' 
  = '';
  static EXTERNAL_FEATURES_DATA: IFeatureData = EXTERNAL_AVAILABLE_FEATURES;
  static INTERNAL_FEATURES_DATA: IFeatureData = INTERNAL_AVAILABLE_FEATURES;
  static TOTAL_FEATURES = TOTAL_FEATURES;
  static EXTERNAL_REVEALEAD = false;
  static INTERNAL_REVEALEAD = false;
  static IS_PRE_VENDA = false;
  static assets: {
    car: GLTF;
    carSeats: GLTF;
    featureHits: GLTF;
    featureHitsInterior: GLTF;
    featurePaths: GLTF;
    eyeSprites: GLTF;
    mobileBoxes: GLTF;
    rectLights: GLTF;
    radialEnvs: GLTF;    
    interior: THREE.Texture;
    interiorAlpha: THREE.Texture;
    hdr: THREE.Texture;
    flakes: THREE.Texture;
    floorDiffuseMap: THREE.Texture; 
    floorNormalMap: THREE.Texture;
    floorRoughnessMap: THREE.Texture;
    floorMetalnessMap: THREE.Texture;
    floorAlphaMap: THREE.Texture;
    backgroundBase: THREE.Texture;
    backgroundNoise: THREE.Texture;
    fireflyParticle: THREE.Texture;
    fireSparkNoise: THREE.Texture;
    particle: THREE.Texture;
    hdrCar: THREE.Texture;
    hdrInterior: THREE.Texture;
    carBlackoutTexture: THREE.Texture;
    transitionTexture: THREE.Texture;
  };

  static loader = new Loader();

  static BLOOM_SCENE = 1;
  static bloomLayer = new THREE.Layers();

  static debugMode: boolean;
  static freeCamera = false;
  static showControls = false;
  static editMode = false;
  static hasOverlay = false;
  static directionalLight: THREE.DirectionalLight;

  static sprites: SpriteManager = new SpriteManager();
  static startedExperience = false;
  static gpuTier : TierResult | null = null; 

  static setup = async (
    canvas: HTMLCanvasElement,
    onLoadedPercentage: (value: number) => void
  ) => {
    if (ScenesManager.allCreated || !canvas) return; 
    return new Promise(async (resolve) => {
      // ScenesManager.debugMode = parseUrlQuery().debugScene === "true";
      // ScenesManager.showControls = parseUrlQuery().showControls === "true";
      // ScenesManager.freeCamera = parseUrlQuery().freeCamera === "true";
      // ScenesManager.editMode = parseUrlQuery().editMode === "true";

      onLoadedPercentage(0.1);
      ScenesManager.loader.setup();
      EventsManager.setup();

      const carFile = isMobile() ? "car_low.glb" : "car.glb";
      
      const [car, carSeats, featureHits, featureHitsInterior, featurePaths, eyeSprites, mobileBoxes, rectLights, radialEnvs] = await ScenesManager.loader.loadModels([
        `${ScenesManager.basePath}/assets/models/${carFile}`,
        `${ScenesManager.basePath}/assets/models/car-seats.glb`,
        `${ScenesManager.basePath}/assets/models/feature-hits.glb`,
        `${ScenesManager.basePath}/assets/models/feature-hits-interior.glb`,
        `${ScenesManager.basePath}/assets/models/car-paths.glb`,
        `${ScenesManager.basePath}/assets/models/eye-sprites.glb`,
        `${ScenesManager.basePath}/assets/models/mobile-boxes.glb`,
        `${ScenesManager.basePath}/assets/models/car-rect-lights.glb`,
        `${ScenesManager.basePath}/assets/models/feature-radial-envs.glb`,  
      ]);
      onLoadedPercentage(0.6);
      const [hdr, hdrCar, hdrInterior] = await ScenesManager.loader.loadHdr([
        `${ScenesManager.basePath}/assets/textures/blackout-hdr.hdr`,
        `${ScenesManager.basePath}/assets/textures/hdr/custom.hdr`,
        `${ScenesManager.basePath}/assets/textures/hdr/custom_pb.hdr`
      ])
      onLoadedPercentage(0.7);
      const [
        flakes,
        floorDiffuseMap, 
        floorNormalMap,
        floorRoughnessMap,
        floorMetalnessMap,
        floorAlphaMap,
        backgroundBase,
        backgroundNoise,
        fireflyParticle,
        fireSparkNoise,
        particle,
        interior,
        interiorAlpha,
        carBlackoutTexture,
        transitionTexture,

      ] = await ScenesManager.loader.loadTextures([
        `${ScenesManager.basePath}/assets/textures/car/flakes.png`,
        // `${ScenesManager.basePath}/assets/textures/floor/floor_test/plastered_stone_wall_diff_gray_2k.jpg`,
        // `${ScenesManager.basePath}/assets/textures/floor/floor_test/plastered_stone_wall_nor_gl_2k.jpg`,
        // `${ScenesManager.basePath}/assets/textures/floor/floor_test/plastered_stone_wall_ao_2k.jpg`,
        // `${ScenesManager.basePath}/assets/textures/floor/floor_test/plastered_stone_wall_arm_2k.jpg`,
        `${ScenesManager.basePath}/assets/textures/floor/new/GROUND_01_basecolor.png`,
        `${ScenesManager.basePath}/assets/textures/floor/new/GROUND_01_normal.png`,
        `${ScenesManager.basePath}/assets/textures/floor/new/GROUND_01_roughness.png`,
        `${ScenesManager.basePath}/assets/textures/floor/new/GROUND_01_metallic.png`,
        `${ScenesManager.basePath}/assets/textures/floor/concrete_floor_worn_001_alpha_2k.jpg`,
        `${ScenesManager.basePath}/assets/textures/background/galaxybase.png`,
        `${ScenesManager.basePath}/assets/textures/background/flowmapnoise.png`,
        `${ScenesManager.basePath}/assets/textures/background/rad-grad.png`,
        `${ScenesManager.basePath}/assets/textures/background/fireSparkNoise.jpg`,
        `${ScenesManager.basePath}/assets/textures/car/spark1.png`,
        `${ScenesManager.basePath}/assets/textures/car/360-map3.jpg`,
        `${ScenesManager.basePath}/assets/textures/car/360-alpha.jpg`,
        `${ScenesManager.basePath}/assets/textures/car/blackout.jpg`,
        `${ScenesManager.basePath}/assets/textures/transition3.png`,

      ]);
      ScenesManager.assets = {
        car,
        carSeats,
        featureHits,
        featureHitsInterior,
        featurePaths,
        eyeSprites,
        mobileBoxes,
        rectLights,
        radialEnvs,        
        hdr,
        hdrCar,
        hdrInterior,
        flakes,
        floorDiffuseMap, 
        floorNormalMap,
        floorRoughnessMap,
        floorMetalnessMap,
        floorAlphaMap,
        backgroundBase,
        backgroundNoise,
        fireflyParticle,
        fireSparkNoise,
        particle,
        interior,
        interiorAlpha,
        carBlackoutTexture,
        transitionTexture
      };
      ScenesManager.assets.carBlackoutTexture.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.carBlackoutTexture.wrapS = ScenesManager.assets.carBlackoutTexture.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.carBlackoutTexture.flipY = false;
      ScenesManager.assets.interior.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.interior.wrapS = ScenesManager.assets.particle.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.interiorAlpha.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.interiorAlpha.wrapS = ScenesManager.assets.particle.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.particle.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.particle.wrapS = ScenesManager.assets.particle.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.fireSparkNoise.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.fireSparkNoise.wrapS = ScenesManager.assets.fireSparkNoise.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.flakes.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.flakes.wrapS = ScenesManager.assets.flakes.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.flakes.repeat.set(32, 32);
      ScenesManager.assets.hdr.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.hdr.mapping = THREE.EquirectangularReflectionMapping;
      ScenesManager.assets.hdrCar.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.hdrCar.mapping = THREE.EquirectangularReflectionMapping;
      ScenesManager.assets.hdrInterior.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.hdrInterior.mapping = THREE.EquirectangularReflectionMapping;
      const floorDivision = 8;
      ScenesManager.assets.floorAlphaMap.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.floorDiffuseMap.colorSpace = THREE.SRGBColorSpace; 
      ScenesManager.assets.floorDiffuseMap.repeat = new THREE.Vector2( 
        floorDivision,
        floorDivision
      );
      ScenesManager.assets.floorDiffuseMap.wrapS = THREE.RepeatWrapping; 
      ScenesManager.assets.floorDiffuseMap.wrapT = THREE.RepeatWrapping; 
      ScenesManager.assets.floorDiffuseMap.anisotropy = 8;

      ScenesManager.assets.floorNormalMap.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.floorNormalMap.repeat = new THREE.Vector2(
        floorDivision,
        floorDivision
      );
      ScenesManager.assets.floorNormalMap.wrapS = THREE.RepeatWrapping;
      ScenesManager.assets.floorNormalMap.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.floorNormalMap.anisotropy = 8;

      ScenesManager.assets.floorRoughnessMap.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.floorRoughnessMap.repeat = new THREE.Vector2(
        floorDivision,
        floorDivision
      );
      ScenesManager.assets.floorRoughnessMap.wrapS = THREE.RepeatWrapping;
      ScenesManager.assets.floorRoughnessMap.wrapT = THREE.RepeatWrapping;
      ScenesManager.assets.floorRoughnessMap.anisotropy = 8;

      ScenesManager.assets.floorMetalnessMap.colorSpace =
        THREE.SRGBColorSpace;

      ScenesManager.assets.floorMetalnessMap.repeat =
        new THREE.Vector2(24, 24);
      ScenesManager.assets.floorMetalnessMap.wrapS =
        THREE.RepeatWrapping;
      ScenesManager.assets.floorMetalnessMap.wrapT =
        THREE.RepeatWrapping;
      ScenesManager.assets.floorMetalnessMap.anisotropy = 8;
      

      ScenesManager.assets.backgroundBase.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.floorDiffuseMap.repeat = new THREE.Vector2(24, 24); 
      ScenesManager.assets.backgroundBase.wrapS = THREE.RepeatWrapping;
      ScenesManager.assets.backgroundBase.wrapT = THREE.RepeatWrapping;

      ScenesManager.assets.backgroundNoise.colorSpace = THREE.SRGBColorSpace;
      ScenesManager.assets.backgroundNoise.wrapS = THREE.RepeatWrapping;
      ScenesManager.assets.backgroundNoise.wrapT = THREE.RepeatWrapping;

      onLoadedPercentage(0.9);

      ScenesManager.clock = new THREE.Clock();
      ScenesManager.raycaster = new THREE.Raycaster();
      ScenesManager.pointer = new THREE.Vector2();

      if (ScenesManager.showControls) { 
        ScenesManager.tweakPane = new Pane();
      }


      ScenesManager.bloomLayer.set( ScenesManager.BLOOM_SCENE );

      ScenesManager.gpuTier = await getGPUTier();

      ScenesManager.mainScene = new StandardScene(
        canvas,
        this.onStarted,
        this.onUpdate
      );
      

      resolve(true);
    });
  };

  static buildScene() {
    ScenesManager.mainScene.build();
    ScenesManager.sprites.setup()
    // StatsManager.setup()

    window.addEventListener(
      "pointermove",
      ScenesManager.onDocumentGetPointer
    );
    window.addEventListener(
      "pointerdown",
      ScenesManager.onDocumentGetPointer
    );

    window.addEventListener("resize", () => ScenesManager.resize());
  }

  static featuresParse(data: {[name: string]: string}[]) {
    let exteriorCount = 0;
    let interiorCount = 0;    
    return new Promise(resolve => {
      data.forEach((feat: any) => {
        for (let key in feat.exterior) {
          if (ScenesManager.EXTERNAL_FEATURES_DATA[key] && feat.exterior[key]) {
            ScenesManager.EXTERNAL_FEATURES_DATA[key].active = feat.exterior[key].active;
            ScenesManager.EXTERNAL_FEATURES_DATA[key].lastEditedTime = feat.exterior[key].lastEditedTime;
            COPY[supportedLanguages.es].features[key].description = feat.exterior[key].content;
            
            if (feat.exterior[key].active) {
              exteriorCount++;

              if (!ScenesManager.lastAddedFeature)
              {
                ScenesManager.lastAddedFeature = {...feat.exterior[key], name: key}
              }
              else
              {
                let currentDate = new Date(ScenesManager.lastAddedFeature.lastEditedTime)
                let newDate = new Date(feat.exterior[key].lastEditedTime)
        
                //if new feature is newer
                if (newDate > currentDate)
                {
                  ScenesManager.lastAddedFeature = {...feat.exterior[key], name: key}
                }
              }
            }
          }
        }
        for (let key in feat.interior) {
          if (ScenesManager.INTERNAL_FEATURES_DATA[key] && feat.interior[key]) {
            ScenesManager.INTERNAL_FEATURES_DATA[key].active = feat.interior[key].active;
            COPY[supportedLanguages.es].features[key].description = feat.interior[key].content;

            if (feat.interior[key].active) {
              interiorCount++;

              if (!ScenesManager.lastAddedFeature)
              {
                ScenesManager.lastAddedFeature = {...feat.interior[key], name: key}
              }
              else
              {
                let currentDate = new Date(ScenesManager.lastAddedFeature.lastEditedTime)
                let newDate = new Date(feat.interior[key].lastEditedTime)
        
                //if new feature is newer
                if (newDate > currentDate)
                {
                  ScenesManager.lastAddedFeature = {...feat.interior[key], name: key}
                }
              }
            }
          }
        }
      });
  
      const externalLen = Object.keys(ScenesManager.EXTERNAL_FEATURES_DATA).length;
      const internalLen = Object.keys(ScenesManager.INTERNAL_FEATURES_DATA).length;
      ScenesManager.TOTAL_FEATURES = {
        external: externalLen,
        externalActive: exteriorCount,
        internal: internalLen,
        internalActive: interiorCount,
        all: internalLen + externalLen,
      }
      resolve(true);
    })
  }

 

  static onDocumentGetPointer(event: { clientX: number; clientY: number }) {
    ScenesManager.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    ScenesManager.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  static resize() {
    ScenesManager.mainScene.resize();
    // ScenesManager.mainScene.composer?.setSize(width, window.innerHeight);

    // if (this.effectFocus) { 
    //   this.effectFocus.uniforms["screenWidth"].value = 
    //     width * window.devicePixelRatio;
    //   this.effectFocus.uniforms["screenHeight"].value = 
    //     window.innerHeight * window.devicePixelRatio;
    // }
  }

  static onStarted = async () => {};

  static onUpdate = () => {
  //  StatsManager.stats.begin()
    ScenesManager.mainScene?.render();
    ScenesManager.raycaster.setFromCamera(
      ScenesManager.pointer,
      ScenesManager.mainScene.camera
    );
  //  StatsManager.stats.end()
  };

  static dispose() {
    if (!ScenesManager.mainScene || !ScenesManager.mainScene.renderer) return; 
    ScenesManager.mainScene?.stop();
    ScenesManager.mainScene?.renderer.setAnimationLoop(null);

    ScenesManager.clock?.stop();

    ScenesManager.allCreated = false;
  }
}
